/**
 * Shared Mapbox helper utilities.
 *
 * These pure helpers were previously duplicated across several map components
 * (Devices, Geofences, Device, VirtualizedDeviceMarkers, AssetMap layers,
 * widget maps). Centralising them keeps the behaviour consistent and trims the
 * size of the individual map components.
 */

/**
 * Resolve the underlying mapbox-gl map instance from a react-map-gl ref.
 *
 * @param {{ current: any }} mapRef - React ref returned by react-map-gl.
 * @returns {object|null} The mapbox-gl map instance, or null when unavailable.
 */
export const getMapboxMap = (mapRef) => {
  const current = mapRef?.current;
  if (!current) {
    return null;
  }

  return typeof current.getMap === "function" ? current.getMap() : current;
};

/**
 * Validate a longitude/latitude pair.
 *
 * Rejects null/undefined, non-finite numbers, the (0, 0) null-island value, and
 * out-of-range coordinates.
 *
 * @param {number|string} lng - Longitude.
 * @param {number|string} lat - Latitude.
 * @returns {boolean} True when the coordinate is usable for rendering.
 */
export const isValidCoordinate = (lng, lat) => {
  if (lng == null || lat == null) return false;
  const lngNum = Number(lng);
  const latNum = Number(lat);
  if (!Number.isFinite(lngNum) || !Number.isFinite(latNum)) return false;
  if (lngNum === 0 || latNum === 0) return false;
  if (lngNum < -180 || lngNum > 180) return false;
  if (latNum < -90 || latNum > 90) return false;
  return true;
};

/**
 * Compute a bounding box for a Polygon or MultiPolygon geometry.
 *
 * @param {{ type: string, coordinates: any[] }} geometry - GeoJSON geometry.
 * @returns {[[number, number], [number, number]]|null} `[[minLng, minLat], [maxLng, maxLat]]` or null.
 */
export const getBoundsFromGeometry = (geometry) => {
  const coords = [];
  const collect = (c) => {
    if (typeof c[0] === "number") {
      coords.push(c);
    } else {
      c.forEach(collect);
    }
  };
  if (geometry?.type === "Polygon" || geometry?.type === "MultiPolygon") {
    collect(geometry.coordinates);
  }
  if (!coords.length) return null;
  const lngs = coords.map((c) => c[0]);
  const lats = coords.map((c) => c[1]);
  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ];
};

/**
 * Ray-casting point-in-polygon test (handles Polygon and MultiPolygon).
 *
 * @param {[number, number]} point - `[lng, lat]` point to test.
 * @param {{ type: string, coordinates: any[] }} geometry - GeoJSON geometry.
 * @returns {boolean} True when the point falls inside the geometry.
 */
export const pointInPolygon = ([x, y], geometry) => {
  const rings =
    geometry.type === "Polygon"
      ? [geometry.coordinates[0]]
      : geometry.type === "MultiPolygon"
        ? geometry.coordinates.map((p) => p[0])
        : [];
  return rings.some((ring) => {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  });
};
